import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { formatDateAt } from "simplydo/core/dates";
import { getImageUrl } from "simplydo/core/images";
import { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Icon } from "simplydo/react/ui";

import { useAppSelector } from "store";
import util from "utils/utils";

import type { FeedCardHeaderProps, FeedCardHeaderSubject } from "../types";

const isNamedEntity = (
  subject: FeedCardHeaderSubject,
): subject is OpenAPI.Schemas["Organisation"] | OpenAPI.Schemas["Company"] =>
  "name" in subject && !("profile" in subject && subject.profile);

const StyledAvatar = styled(Link)<{ $logoUrl: string | null | undefined }>`
  position: relative;
  height: 30px !important;
  width: 30px !important;
  line-height: inherit;
  border-radius: 50%;
  display: inline-block;
  background-size: cover;
  background-position: center center;
  vertical-align: middle;
  margin-right: 5px;
  background-image: ${({ $logoUrl }) =>
    $logoUrl ? `url(${$logoUrl})` : "none"};
`;

const StyledCompanyLogo = styled.div<{ $company: OpenAPI.Schemas["Company"] }>`
  position: relative;
  height: 30px !important;
  width: 30px !important;
  line-height: inherit;
  border-radius: 50%;
  display: inline-block;
  background-size: cover;
  background-position: center center;
  vertical-align: middle;
  margin-right: 5px;
  background-image: ${({ $company }) =>
    `url(${$company?.images?.logo?.url || $company.imageUrl})`};
`;

export type { FeedCardHeaderProps };

const isPreparedUser = (
  subject: FeedCardHeaderSubject,
): subject is OpenAPI.Schemas["PreparedUser"] => "profile" in subject;

const FeedCardHeader = ({
  user,
  action,
  date,
  subjectItem,
  ownerType,
  company,
  subjectItem2,
  subjectItem3,
}: FeedCardHeaderProps) => {
  const currentUser = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const theme = useTheme();
  const subject: FeedCardHeaderSubject | undefined =
    user && currentUser && user._id === currentUser._id ?
      { ...currentUser, ...user }
    : user || undefined;
  let name: string;
  if (ownerType === "company" && company) {
    name = company.legalName || company.name || "";
  } else if (subject && isPreparedUser(subject) && subject.profile) {
    name = subject.profile.fullName || "Someone";
  } else {
    name = t("generic.anonymous");
  }
  const profilePath =
    subject && isPreparedUser(subject) && subject.profile ?
      "profiles"
    : "users";

  const imageUrl =
    user && subject ?
      isNamedEntity(subject) ?
        getImageUrl(
          subject,
          "logo",
          ["200x200"],
          "images/defaults/profile-logo.png",
        )
      : util.avatarUrl(subject as OpenAPI.Schemas["PreparedUser"])
    : undefined;

  return (
    <div style={{ width: "100%", color: "#000", paddingBottom: 0 }}>
      <div
        style={{
          float: "left",
          display: "flex",
          alignItems: "center",
          color: "gray",
          gap: 5,
          maxWidth: "90%",
        }}
      >
        {ownerType === "company" && company ?
          <StyledCompanyLogo $company={company} />
        : user && subject ?
          <StyledAvatar
            $logoUrl={imageUrl}
            to={`/${profilePath}/${subject._id}`}
            style={{ minWidth: 32, marginRight: 6, marginLeft: 3 }}
          />
        : <Icon
            size="large"
            name="user secret"
            style={{
              color: "rgb(150,150,150)",
              marginRight: 6,
              marginLeft: 3,
              minWidth: 32,
            }}
          />
        }
        {t(action, { name, subjectItem, subjectItem2, subjectItem3 })}
      </div>
      {!theme.sizes.isMobile && (
        <div
          style={{
            float: "right",
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
            lineHeight: 2.5,
            color: "rgb(180,180,180)",
            fontSize: 10,
          }}
        >
          {formatDateAt(date)}
        </div>
      )}
    </div>
  );
};

export default FeedCardHeader;
